import React, { useContext, useEffect, useState } from 'react';
import { LocaleSwitcherContainer } from 'components/locale-switcher/exp/container';
import { getTenantById, isTenantExpCA, isTenantExpUS } from 'tenants';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { buildClassName } from 'utils/build-class-name';
import Link from 'components/link';
import { useUserContext } from 'contexts/user';
import { IModalContext, ModalContext } from 'contexts/modal';
import MenuButton from 'components/header/menu-button';
import ThemedIcon from 'components/themed-icon';
import FakeAdvancedSearch from 'components/search-bar/advanced-search/fake-search';
import styles from './style.module.scss';
import { headerIds, testIds } from 'constants/test-constants';
import { useIsDesktop, useIsMobile } from 'hooks/use-size-class';
import { useJoinExpMenuItems } from 'hooks/use-join-exp-menu-items';
import { useResponsiveHeaderButton } from 'hooks/use-responsive-header-button';
import { useFeaturesContext } from 'contexts';
import Button from 'components/control/button';
import UserIcon from 'components/icon/user-icon';
import { PATHS_WITH_FLOATING_HEADERS, PATHS_WITH_SCROLLED_HEADERS } from 'constants/ui-headers';
import SquareSearchIcon from 'components/icon/square/search-icon';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { usePreferencesContext } from 'contexts/preferences';
import { useTrackExpOnlyEvents } from 'hooks/use-track-exp-only-events';
import {
  GTM_CLICK_CONTACT_US_BUTTON_JOIN_EXP,
  GTM_EXP_FIND_YOUR_HOME_CLICK_HOME_PAGE,
  GTM_EXP_FIND_YOUR_HOME_CLICK_UNIVERSAL,
  GTM_SIMPLE_CHAT_SAVE_SEARCH,
} from 'constants/events';
import ImmersiveSearch from 'components/search-bar/immersive-search';
import Dropdown from 'components/dropdown';
import { TenantId } from '@tenants/types';
import { getJoinExpConfig, getTenantOrganizationConfig } from '@tenants/utils/configs';
import { useTranslatedHeaderConfig } from 'hooks/use-translated-header-config';
import LoadingText from 'components/loading-text';
import { AnalyticsController } from 'analytics/analytics-controller';
import { INTERNATIONAL_LINKS_MODAL } from 'contexts/modal';
import { Language as GlobeIcon } from '@mui/icons-material';
import { useSmartChatMenuItem } from 'hooks/use-smartchat-menu-item';
import { getSmartChatBetaStatus, SmartChatBetaStatus } from 'utils/cookies';

type HeaderProps = {
  tenantId: TenantId;
  showAdvancedSearch?: boolean;
  isSearchOrAreaPage?: boolean;
  isShowing404Page?: boolean;
  joinExpFabDestination?: string;
};


// Proper dynamic import at module level
const AdvancedSearch = dynamic(() => import('components/search-bar/advanced-search'), {
  loading: () => <LoadingText />,
  ssr: false,
});


const Header = ({
  tenantId,
  showAdvancedSearch = true,
  isSearchOrAreaPage = false,
  isShowing404Page = false,
  joinExpFabDestination,
}: HeaderProps) => {
  const tenant = getTenantById(tenantId);
  const { authMenuItems, menuItems: baseMenuItems, mainButtonCopy, authButtonCopy, buttonLabels } = useTranslatedHeaderConfig(tenantId);
  const { isAuthenticated, user, isCrawler } = useUserContext();
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { isSearchPanelOpen, setIsSearchPanelOpen, features } = useFeaturesContext();
  const { setIsMapSearchOpen, isMapSearchOpen, listingParams, isAiSearch } = usePreferencesContext();
  const [menuDropdownIsActive, setMenuDropdownIsActive] = useState(isCrawler);
  const [userDropdownIsActive, setUserDropdownIsActive] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();
  const router = useRouter();
  const { trackExpEvent } = useTrackExpOnlyEvents();
  const isFloating = PATHS_WITH_FLOATING_HEADERS.includes(router.pathname) && !isShowing404Page;
  const useScrolledHeader = PATHS_WITH_SCROLLED_HEADERS.includes(router.pathname);

  const isAgentSearch = router.pathname === '/agents-search';
  const isHomePage = router.pathname === '/';
  const isSearchPage = router.pathname === '/search';
  const isExpUsTenant = isTenantExpUS(tenant.id);
  const isLocalFocusHomepageEnabled = (tenant.features.UseLocalFocusHomepage ?? false) || (features?.['useLocalFocusHomepage'] === true);
  const isJoinExpUs = isTenantExpUS(tenant.id) && router.pathname.startsWith('/join-exp-us');
  const isJoinExpCa = isTenantExpCA(tenantId) && router.pathname.startsWith('/join-exp-ca');
  const isJoinExp = isJoinExpUs || isJoinExpCa;
  const isJoinExpCaHome = isTenantExpCA(tenantId) && router.pathname === '/join-exp-ca';
  const isDesktop = useIsDesktop();
  const joinExpConfig = getJoinExpConfig(tenant);
  const joinPageContactPath = joinExpConfig?.joinPageContactPath;
  const joinPageContactPage = joinExpConfig?.joinPageContactPage;
  const joinNowDestination = joinExpFabDestination ?? joinExpConfig?.fabDestination;


  const aiSearchFeatureFlagEnabled = tenant.features.AiSearch ?? false;
  const isAiSearchFeatureEnabled = aiSearchFeatureFlagEnabled && (getSmartChatBetaStatus() === SmartChatBetaStatus.OPTED_IN);
  // Only enabled if feature flag is on, user has opted in and currently viewing AI search

  const isAiChatEnabled = isSearchPage && isAiSearchFeatureEnabled && isAiSearch;
  const aiSearchEnabledMobile = isSearchPage && isMobile && isAiSearchFeatureEnabled && isAiSearch;
  const aiSearchEnabled = isSearchPage && isAiSearchFeatureEnabled && isAiSearch;

  const searchSectionOutsideClickHandler = useOutsideClickHandler<HTMLDivElement>(() => { setIsSearchPanelOpen(false); setIsMapSearchOpen(false); });

  // Manage Join eXp menu items and button visibility
  const { menuItems: joinExpMenuItems, shouldShowContactUsButton, getJoinExpContactUrl } = useJoinExpMenuItems({
    isJoinExp,
    baseMenuItems,
    buttonLabels,
    joinPageContactPath,
    joinPageContactPage,
    trackExpEvent,
  });

  // Add SmartChat Beta menu item
  const { menuItems } = useSmartChatMenuItem({
    baseMenuItems: joinExpMenuItems,
    isFeatureEnabled: tenant.features.AiSearch ?? false,
  });

  // Manage responsive button display to prevent logo shrinkage
  const { shouldShowIconOnly } = useResponsiveHeaderButton();

  const toggleMenuDropdown = () => {
    setMenuDropdownIsActive(prev => !prev);
  };
  const toggleUserDropdown = () => {
    setUserDropdownIsActive(prev => !prev);
  };

  const generateAuthUserLabel = () => {
    if (user) {
      if (user.firstName || user.lastName) {
        return `${(user?.firstName || '').slice(0, 1)}${(user?.lastName || '').slice(0, 1)}`;
      }
      return user?.email?.slice(0, 1);
    }
    return authButtonCopy;
  };
  const authUserLabel = generateAuthUserLabel();

  const handleScroll = () => setIsScrolled(window.scrollY > 40);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleSearch = () => {
    if (isMapSearchOpen) {
      setIsMapSearchOpen(false);
    } else {
      const newValue = !isSearchPanelOpen;
      setIsSearchPanelOpen(newValue);
    }
  };

  const SearchOrAreaPageButton = () => (
    <div className={styles.search} ref={searchSectionOutsideClickHandler}>
      <Button
        Icon={isMobile && SquareSearchIcon}
        onClick={toggleSearch}
        className={buildClassName(styles.auth, isMobile && styles.user, (isSearchPanelOpen || isMapSearchOpen) && styles.active)}
      />
      {isMapSearchOpen && !isSearchPanelOpen && isExpUsTenant && (
        <div className={buildClassName(styles.panel, styles['no-background-color'])} onClick={() => { setIsSearchPanelOpen(true); setIsMapSearchOpen(false); }}>
          <FakeAdvancedSearch />
        </div>)}
      {isSearchPanelOpen && (
        <div className={styles.panel}>
          <AdvancedSearch isInPanel={true} isFilterButtonHidden={true} hideSearchPanel={() => setIsSearchPanelOpen(false)} />
          <div className={styles['full-screen-search']} />
        </div>
      )}
    </div>
  );

  const JoinExpContent = ({ url }: { url: string }) => (
    <Link onClick={() => trackExpEvent(GTM_CLICK_CONTACT_US_BUTTON_JOIN_EXP)} href={url} target="_blank" className={styles['find-your-home']}>
      <Button tabIndex={-1} label={buttonLabels?.contactUs} className={styles.search} />
    </Link>
  );

  const JoinNowContent = () => (
    <Link href={joinNowDestination || ''} target="_blank" rel="noreferrer" className={styles['find-your-home']}>
      <Button tabIndex={-1} label="Join Now" className={styles.search} />
    </Link>
  );

  const DefaultContent = () => (
    <Link onClick={() => {
      if (isHomePage) {
        trackExpEvent(GTM_EXP_FIND_YOUR_HOME_CLICK_HOME_PAGE);
        trackExpEvent(GTM_EXP_FIND_YOUR_HOME_CLICK_UNIVERSAL);
      } else {
        trackExpEvent(GTM_EXP_FIND_YOUR_HOME_CLICK_UNIVERSAL);
      }
    }}
    href={isExpUsTenant ? '/search?openSearch=true' : '/search'} className={styles['find-your-home']} >
      <Button 
        tabIndex={-1} 
        label={shouldShowIconOnly ? '' : mainButtonCopy}
        Icon={shouldShowIconOnly ? SquareSearchIcon : undefined}
        className={buildClassName(styles.search, styles['exp-us-button'], shouldShowIconOnly && styles.user)} 
      />
    </Link>
  );

  const HeaderButton = () => {
    if (isSearchOrAreaPage && isMobile) {
      return <SearchOrAreaPageButton />;
    } else if (isJoinExp) {
      return (
        <>
          <JoinNowContent />
          {shouldShowContactUsButton && <JoinExpContent url={getJoinExpContactUrl()} />}
        </>
      );
    } else if (!isJoinExp) {
      return <DefaultContent />;
    }
    return null;
  };

  const renderHeaderOrSearchBar = () => {
    if (isDesktop) {
      if (isSearchOrAreaPage) return null;
      if (isJoinExp || isJoinExpCa || isJoinExpUs) return <HeaderButton />;
      if ((!isHomePage)) return <ImmersiveSearch />;
    }
    return <HeaderButton />;
  };

  const isSwitcherOverrideEnabledForExpCa = tenant.localization?.allowGlobalLocaleSwitching === true;
  const canChangeLanguage = isJoinExp || isSwitcherOverrideEnabledForExpCa;

  const handleSaveSearchButtonClick = () => {
    AnalyticsController.trackEvent(GTM_SIMPLE_CHAT_SAVE_SEARCH);
    if (isAuthenticated) {
      openModal('create-saved-search', { query: { sort: listingParams.sort, filter: listingParams.filter }});
    } else {
      openModal('login-registration');
    }
  };


  return (
    <div className={buildClassName(styles.component, isFloating && styles.home, isAgentSearch && styles['agent-search'], (isScrolled || useScrolledHeader) && styles.scrolled, isExpUsTenant && isHomePage && !isLocalFocusHomepageEnabled && styles['exp-us'], aiSearchEnabled && styles['ai-search-enabled'])} data-testid={headerIds.header}>
      <div className={buildClassName(styles['layout-container'], aiSearchEnabledMobile && styles['ai-search-enabled-mobile'])}>
        <div className={buildClassName(styles['logo-wrapper'])} data-testid={headerIds.zoocasaHeaderLogo}>
          <Link href="/" as="/" className={styles.logo} rel="noreferrer" ><ThemedIcon /></Link>
        </div>

        <div className={styles.navigation}>
          {isAiChatEnabled && <Button label="Save Search" onClick={handleSaveSearchButtonClick} />}
          {isJoinExpCaHome && !isMobile && (
            <Link href={'/commercial-waitlist-ca'} className={styles['find-your-home']}>
              <Button tabIndex={-1} label={buttonLabels?.commercialWaitlist} className={styles.search} />
            </Link>
          )}
          {renderHeaderOrSearchBar()}
          <Button
            label=""
            Icon={GlobeIcon}
            onClick={() => openModal(INTERNATIONAL_LINKS_MODAL, getTenantOrganizationConfig(tenant).internationalLinks)}
            className={buildClassName(styles.international, isMobile && styles.mobile)}
          />
          {canChangeLanguage && <LocaleSwitcherContainer tenantId={tenantId} />}
          {!isJoinExp && (isAuthenticated
            ?
            <MenuButton onClick={toggleUserDropdown} closeMenu={() => setUserDropdownIsActive(false)}>
              <span data-testid={headerIds.loggedInUser}>{authUserLabel} {userDropdownIsActive && Dropdown && <Dropdown items={authMenuItems} />}</span>
            </MenuButton>
            :
            <Button
              label={!isMobile ? authUserLabel : ''}
              Icon={isMobile && UserIcon}
              onClick={() => openModal('login-registration', { roleSelection: tenant.features.LoginRoleSelection ?? false })}
              className={buildClassName(styles.auth, isMobile && styles.user)}
            />
          )}
          <MenuButton className={styles.menu} testId={testIds.menuButton} onClick={toggleMenuDropdown} closeMenu={() => setMenuDropdownIsActive(false)}>
            <>
              <div className={styles['bar-container']} id="menu">
                <span className={buildClassName(styles.bar, menuDropdownIsActive && styles.x)} />
                <span className={buildClassName(styles.bar, menuDropdownIsActive && styles.x)} />
              </div>
              {menuDropdownIsActive && Dropdown && <Dropdown items={menuItems} />}
            </>
          </MenuButton>
        </div>
        <>
          {isSearchPanelOpen && showAdvancedSearch && (
            <div className={styles.panel}>
              <AdvancedSearch isFilterButtonHidden={true} isInPanel hideSearchPanel={() => setIsSearchPanelOpen(false)} testId={testIds.advancedSearchMobile} />
              <div className={styles['full-screen-search']} />
            </div>
          )}
        </>
      </div>
    </div>
  );
};

export default Header;
